import { Cluster, ClusterStats, Marker, MarkerUtils } from "@googlemaps/markerclusterer";
import {
	ClusterContextMenuResponse,
	IClusterRenderer,
	IMapEventListener,
	MapEventListenerEventNames,
	MapMarkerClustererOptions,
	MarkerMetadataRef
} from "@sureview/v2-mapping-saas";

export class MapGoogleClusterRenderer implements IClusterRenderer {
    private readonly _mapMarkerClustererOptions: MapMarkerClustererOptions;

    /**
     * @constructor Default constructor for MapGoogleClusterRenderer.
     * @param {MapMarkerClustererOptions} mapMarkerClustererOptions The map marker clusterer options
     */
    constructor(mapMarkerClustererOptions: MapMarkerClustererOptions) {
        this._mapMarkerClustererOptions = mapMarkerClustererOptions;
    }

    /**
     * @summary Gets the contextMenu event listener.
     */
    private get contextMenuEventListener(): IMapEventListener | null {
        return this._mapMarkerClustererOptions
            .mapEventListeners
            ?.find((listener: IMapEventListener) =>
                listener.eventName === MapEventListenerEventNames.ContextMenu) ?? null;
    }

    /**
     * @summary Renders the cluster as a marker.
     * @param {Cluster} cluster Cluster details.
     * @param {ClusterStats} _stats The cluster stats.
     * @param {google.maps.Map} map The Google Map.
     * @returns {Marker} Returns a Google Maps Marker/Advanced Marker.
     */
    public render(cluster: Cluster, _stats: ClusterStats, map: google.maps.Map): Marker {
        const color: string = this.getClusterColor(cluster.count);
        const svg: string = this.getIcon(color, cluster.count);
        const title: string = `Cluster of ${cluster.count} markers`;

        let marker: Marker = this.createMarker(map, svg, title, cluster.position);
        this.trySetContextMenuEventListener(marker, cluster, map);
        return marker;
    }

    /**
     * @summary Gets the color of the cluster based on the cluster's count.
     * @param {number} count The count of the cluster.
     * @returns {string} Returns a string which represents the color.
     */
    private getClusterColor(count: number): string {
        const GreenHexColorCode: string = "#03C988";
        const LightBlueHexColorCode: string = "#1C82AD";
        const BlueHexColorCode: string = "#00337C";
        const DarkBlueHexColorCode: string = "#13005A";

        const SmallClusterMaxSize: number = 25;
        const MediumClusterMaxSize: number = 50;
        const LargeClusterMaxSize: number = 100;

        if (count <= SmallClusterMaxSize) {
            return GreenHexColorCode;
        } else if (count > SmallClusterMaxSize && count <= MediumClusterMaxSize) {
            return LightBlueHexColorCode;
        } else if (count > MediumClusterMaxSize && count <= LargeClusterMaxSize) {
            return BlueHexColorCode;
        } else if (count > LargeClusterMaxSize) {
            return DarkBlueHexColorCode;
        }
    }

    /**
     * @summary Gets the icon for the cluster.
     * @param {string} color The color of the icon.
     * @param {number} count The number of items inside the cluster.
     * @returns {string} Returns a string representing the icon as an SVG.
     */
    private getIcon(color: string, count: number): string {
        return `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
            <circle cx="120" cy="120" opacity=".6" r="70" />
            <circle cx="120" cy="120" opacity=".3" r="90" />
            <circle cx="120" cy="120" opacity=".2" r="110" />
            <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
            </svg>`;
    }

    /**
     * @summary Creates the cluster marker.
     * @param {google.maps.Map} map The map.
     * @param {string} svg The marker icon.
     * @param {string} title The title of the marker.
     * @param {google.maps.LatLng} position The position of the marker.
     * @returns {Marker} Returns the marker.
     */
    private createMarker(map: google.maps.Map, svg: string, title: string, position: google.maps.LatLng): Marker {
        if (MarkerUtils.isAdvancedMarkerAvailable(map)) {
            const parser = new DOMParser();
            const svgEl = parser.parseFromString(svg, "image/svg+xml").documentElement;
            svgEl.setAttribute("transform", "translate(0 25)");
            const clusterOptions: google.maps.marker.AdvancedMarkerElementOptions = {
                map,
                position: position,
                title,
                content: svgEl,
            };

            return new google.maps.marker.AdvancedMarkerElement(clusterOptions);
        } else {
            const clusterOptions: google.maps.MarkerOptions = {
                position: position,
                title,
                icon: {
                    url: `data:image/svg+xml;base64,${btoa(svg)}`,
                    anchor: new google.maps.Point(25, 25),
                },
            };

            return new google.maps.Marker(clusterOptions);
        }
    }

    /**
     * @summary Try setting the contextMenu event listener to the marker.
     * @param {Marker} marker The cluster marker.
     * @param {Cluster} cluster The details of the cluster.
     */
    private trySetContextMenuEventListener(marker: Marker, cluster: Cluster, map: google.maps.Map): void {
        if (this.contextMenuEventListener) {
            const response: ClusterContextMenuResponse = {
                markers: cluster.markers.map((marker: Marker) => marker[MarkerMetadataRef]),
                latLng: { lat: cluster.position.lat(), lng: cluster.position.lng() },
            };

            if (MarkerUtils.isAdvancedMarkerAvailable(map)) {
                (marker as google.maps.marker.AdvancedMarkerElement)
                    .content
                    .addEventListener(MapEventListenerEventNames.ContextMenu, (_: Event) => this.contextMenuEventListener.callback(response));
            } else {
                marker.addListener(MapEventListenerEventNames.RightClick, (_: google.maps.MapMouseEvent) => this.contextMenuEventListener?.callback(response));
            }
        }
    }
}
